import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { KpiCard, KpiGrid } from 'molecules';
import { IGroupDetail, useGroupRevenueAggregates } from 'core/hooks';

import { formatMoneyCurrency } from '../_detailShared/format';
import { GroupColors } from './parts/groupColors';

interface IKpiStripProps {
  group: IGroupDetail;
}

interface ICurrencyTotals {
  currency: string;
  budget: number;
  paid: number;
  debt: number;
  studentsWithDebt: number;
  agreementsActive: number;
}

/**
 * KPI strip above the group tabs. Five cards (5th appears only when
 * the group still has a scheduled next lesson):
 *
 *   1. Студенты — enrolled / capacity, with free-seat hint coloured by
 *      `freePlaces` (green ≥1, amber 0, red < 0 over-capacity).
 *   2. Уроки — passed / total, free-lessons sublabel.
 *   3. Доход — Σ `paidAmount` of active agreements, per currency.
 *   4. Долг — Σ max(0, budget − paid), per currency + count of students
 *      who still owe.
 *   5. Следующий урок — `datetimeNextLesson` + countdown (hidden when
 *      `statusFinished`).
 *
 * Phase D source switch (backend TZ §G5): the per-currency rows come
 * solely from `groupRevenueAggregates(groupId)` — a server-side rollup,
 * accurate even when `agreements[].payments[]` would have been too heavy
 * to ship. The roster/agreement payload now lives in the dedicated
 * `GroupRosterQuery`/`GroupFinanceContributionsQuery` (Req 9.1, 9.5), so
 * `IGroupDetail` no longer carries `agreements` and there is no client-side
 * fallback to aggregate over here — the strip renders empty currency rows
 * until the aggregates query resolves.
 */
export const KpiStrip = memo(({ group }: IKpiStripProps) => {
  const { t } = useTranslation();
  const { data: revenue } = useGroupRevenueAggregates(group.id);

  const rows: ICurrencyTotals[] = useMemo(() => {
    const serverRows = (revenue?.rows || []).filter((r): r is NonNullable<
      typeof r
    > => Boolean(r));
    return serverRows.map((r) => ({
      currency: r.currency || '',
      budget: Number(r.budget) || 0,
      paid: Number(r.paid) || 0,
      debt: Number(r.debt) || 0,
      studentsWithDebt: r.studentsWithDebt ?? 0,
      agreementsActive: r.agreementsActive ?? 0,
    }));
  }, [revenue?.rows]);

  const primary = rows[0];
  const isMulti = rows.length > 1;
  const totalDebtors = rows.reduce((acc, r) => acc + r.studentsWithDebt, 0);

  // KPI 1 — capacity tone
  const capacityValue = `${group.studentsCount ?? 0} / ${
    group.maxStudentsCount ?? '—'
  }`;
  const freeSeats = group.freePlaces ?? 0;
  const capacityAccent =
    freeSeats < 0
      ? GroupColors.danger
      : freeSeats === 0
      ? GroupColors.warn
      : GroupColors.success;

  // KPI 2 — lessons progress
  const lessonsValue = `${group.passedLessonsCount ?? 0} / ${
    group.lessonsCount ?? '—'
  }`;

  // KPI 5 — next lesson countdown (hidden when finished)
  const nextLessonInfo = useMemo(() => {
    if (group.statusFinished || !group.datetimeNextLesson) return null;
    const m = moment(group.datetimeNextLesson);
    const days = m.diff(moment(), 'days');
    return {
      label: m.format('DD.MM.YYYY HH:mm'),
      sub:
        days >= 0
          ? t('pages.groupDetail.kpi.nextLessonInDays', { count: days })
          : t('pages.groupDetail.kpi.nextLessonOverdue'),
    };
  }, [group.statusFinished, group.datetimeNextLesson, t]);

  const showNextLesson = Boolean(nextLessonInfo);
  const columns = showNextLesson ? 5 : 4;

  return (
    <div style={{ marginTop: 16 }}>
      <KpiGrid columns={columns}>
        <KpiCard
          label={t('pages.groupDetail.kpi.students')}
          value={capacityValue}
          subLabel={t('pages.groupDetail.kpi.freeSeats', { count: freeSeats })}
          accent={capacityAccent}
        />
        <KpiCard
          label={t('pages.groupDetail.kpi.lessons')}
          value={lessonsValue}
          subLabel={t('pages.groupDetail.kpi.freeLessons', {
            count: group.freeLessonsCount ?? 0,
          })}
        />
        <KpiCard
          label={t('pages.groupDetail.kpi.revenue')}
          value={
            isMulti
              ? rows
                  .map((r) => formatMoneyCurrency(r.paid, r.currency))
                  .join(' · ')
              : formatMoneyCurrency(
                  primary?.paid || 0,
                  primary?.currency || group.currency || '',
                )
          }
          subLabel={
            isMulti
              ? rows
                  .map((r) =>
                    t('pages.groupDetail.kpi.budgetShort', {
                      value: formatMoneyCurrency(r.budget, r.currency),
                    }),
                  )
                  .join(' · ')
              : t('pages.groupDetail.kpi.budgetShort', {
                  value: formatMoneyCurrency(
                    primary?.budget || 0,
                    primary?.currency || group.currency || '',
                  ),
                })
          }
          accent={GroupColors.success}
        />
        <KpiCard
          label={t('pages.groupDetail.kpi.debt')}
          value={
            isMulti
              ? rows
                  .map((r) => formatMoneyCurrency(r.debt, r.currency))
                  .join(' · ')
              : formatMoneyCurrency(
                  primary?.debt || 0,
                  primary?.currency || group.currency || '',
                )
          }
          subLabel={t('pages.groupDetail.kpi.debtors', { count: totalDebtors })}
          accent={
            (primary?.debt || 0) > 0 && !isMulti ? GroupColors.warn : undefined
          }
        />
        {showNextLesson && nextLessonInfo ? (
          <KpiCard
            label={t('pages.groupDetail.kpi.nextLesson')}
            value={nextLessonInfo.label}
            subLabel={nextLessonInfo.sub}
          />
        ) : null}
      </KpiGrid>
    </div>
  );
});
